import { ref, reactive, onMounted } from 'vue'

/**
 * Composable for paginated list fetching.
 *
 * @param {Function} fetchFn - Async function that accepts query and returns the API response
 * @param {Object} defaultQuery - Initial query object (must include pageNum, pageSize)
 * @param {Object} options
 * @param {boolean} options.immediate - Auto-fetch on mount (default: false)
 * @param {Function} options.extract - Custom extractor (default: res => ({ list: res.data.list, total: res.data.total }))
 *
 * @example
 * const { list, loading, total, query, getList, handleSizeChange, handleCurrentChange } =
 *   useList(fetchList, { pageNum: 1, pageSize: 10 })
 *
 * @example flat array response
 * const { list } = useList(fetchAll, defaultQuery, {
 *   extract: res => ({ list: res.data, total: res.data.length })
 * })
 */
export function useList(fetchFn, defaultQuery = {}, options = {}) {
  const {
    immediate = false,
    extract = res => ({ list: res.data.list, total: res.data.total })
  } = options

  const list = ref([])
  const loading = ref(false)
  const total = ref(0)
  const query = reactive({ pageNum: 1, pageSize: 10, ...defaultQuery })

  async function getList() {
    try {
      loading.value = true
      const res = await fetchFn(query)
      const extracted = extract(res)
      list.value = extracted.list ?? []
      total.value = extracted.total ?? 0
    } catch (e) {
      // error handled by request interceptor
    } finally {
      loading.value = false
    }
  }

  function handleSizeChange(val) {
    query.pageNum = 1
    query.pageSize = val
    getList()
  }

  function handleCurrentChange(val) {
    query.pageNum = val
    getList()
  }

  if (immediate) {
    onMounted(getList)
  }

  return { list, loading, total, query, getList, handleSizeChange, handleCurrentChange }
}
